import React, { useCallback, useEffect, useMemo } from "react";
import Draw from "@arcgis/core/views/draw/Draw.js";
import { BaseDraggable } from "../BaseDraggable";
import { useStore } from "../../../stores/store";
import { Area } from "./Area";
import { Distance } from "./Distance";
import { Location } from "./Location";
import {
  updateVertices,
  projectToDecimalDegrees,
  getTextGraphic,
} from "./measureHelpers";
import { DraggableKeys } from "../../../interfaces/config";
import * as areaOperator from "@arcgis/core/geometry/operators/areaOperator";
import * as lengthOperator from "@arcgis/core/geometry/operators/lengthOperator";
import PolyLine from "@arcgis/core/geometry/Polyline";
const key = DraggableKeys.MEASURE_DRAGGABLE;
const unitAbbreviations = {
  feet: "ft",
  meters: "m",
  kilometers: "km",
  miles: "mi",
  yards: "yd",
};

export default function MeasureDraggable() {
  const { isActive, toggleActiveItem, setTooltipText, mapView } = useStore();
  const [currentGraphic, setCurrentGraphic] = React.useState(null);
  const [activeTool, setActiveTool] = React.useState("");
  const [lat, setLat] = React.useState(0);
  const [long, setLong] = React.useState(0);
  const [area, setArea] = React.useState(0);
  const [distance, setDistance] = React.useState(0);
  const [selectedDistanceUnits, setSelectedDistanceUnits] =
    React.useState("feet");
  const [selectedAreaUnits, setSelectedAreaUnits] = React.useState("acres");

  const draw = useMemo(() => {
    if (mapView) {
      return new Draw({ view: mapView });
    }
  }, [mapView]);
  const buttons = useMemo(() => {
    return [
      { label: "Area", value: "area", drawType: "polygon" },
      { label: "Distance", value: "distance", drawType: "polyline" },
      { label: "Location", value: "location", drawType: "point" },
      { label: "Clear", value: "" },
    ];
  }, []);

  useEffect(() => {
    if (!mapView) return;
    mapView.graphics.removeAll();
    if (!currentGraphic) return;
    mapView.graphics.add(currentGraphic);
    const graphic = currentGraphic as __esri.Graphic;
    const geometry = graphic.geometry as __esri.GeometryUnion

    if (activeTool === "area") {
      const area = areaOperator.execute(geometry, { unit: selectedAreaUnits as __esri.AreaUnit });
      setArea(area);
      const distance = lengthOperator.execute(geometry, { unit: selectedDistanceUnits as __esri.LengthUnit });
      setDistance(distance);
    } else if (activeTool === "distance") {
      const distance = lengthOperator.execute(geometry, { unit: selectedDistanceUnits as __esri.LengthUnit });
      setDistance(distance);
    } else if (activeTool === "location") {
      const point = geometry as __esri.Point;
      const [longitude, latitude] = projectToDecimalDegrees(point.x, point.y);
      setLat(latitude);
      setLong(longitude);
    }
    if (geometry.type === "point") {
      return;
    }
    let linePath;
    if (geometry.type === "polyline") {
      linePath = geometry.paths[0];
    } else if (geometry.type === "polygon") {
      if (geometry.rings[0].length > 2) {
        linePath = [
          ...geometry.rings[0],
          geometry.rings[0][0],
        ];
      } else {
        linePath = geometry.rings[0];
      }
    }
    if (linePath && linePath.length > 1) {
      const distanceAbbr = unitAbbreviations[selectedDistanceUnits];
      for (let i = 0; i < linePath.length; i++) {
        const startVertex = linePath[i];
        const endVertex = linePath[i + 1];
        if (!endVertex) {
          break;
        }
        const polyLine = new PolyLine({
          paths: [[startVertex, endVertex]],
          spatialReference: mapView.spatialReference,
        })
        const segmentDistance = lengthOperator.execute(
          polyLine,
          { unit: selectedDistanceUnits as __esri.LengthUnit }
        );
        const textGraphic = getTextGraphic(
          startVertex,
          endVertex,
          parseFloat(segmentDistance.toFixed(2)).toLocaleString() +
          " " +
          distanceAbbr,
          mapView.spatialReference
        );
        mapView.graphics.add(textGraphic);
      }
    }
  }, [
    selectedAreaUnits,
    selectedDistanceUnits,
    setDistance,
    setArea,
    currentGraphic,
    mapView,
    activeTool,
  ]);

  useEffect(() => {
    if (activeTool === "") {
      draw?.reset();
      mapView?.graphics.removeAll();
      setTooltipText("");
      setSelectedAreaUnits("acres");
      setSelectedDistanceUnits("feet");
      setArea(0);
      setDistance(0);
      setLat(0);
      setLong(0);
      setCurrentGraphic(null);
    } else {
      const drawType = buttons?.find((b) => b.value === activeTool)?.drawType;
      if (!drawType || !draw) return;
      const action = draw.create(drawType, { mode: "click" });
      action.on(
        ["vertex-add", "vertex-remove", "cursor-update", "redo", "undo"],
        (e) => updateVertices(e, drawType, mapView, setCurrentGraphic)
      );
      action.on("draw-complete", (e) => {
        updateVertices(e, drawType, mapView, setCurrentGraphic);
        setTooltipText("");
      });

      setTooltipText("Click to start drawing");
    }
  }, [activeTool, setTooltipText]);

  const onButtonClick = useCallback(
    (type: string) => {
      if (activeTool === type) {
        setActiveTool("");
        return;
      }
      setActiveTool(type);
    },
    [activeTool, setActiveTool]
  );

  return (
    <BaseDraggable
      id={key}
      onClose={() => toggleActiveItem(key)}
      visible={isActive(key)}
      initialPosition="top-right"
      headerText="MEASURE"
    >
      <div className="flex items-center justify-around">
        {buttons.map((button, i) => (
          <button
            className={`btn-athena btn-sm ${i === buttons.length - 1 ? "" : "mr-2"
              }`}
            style={{
              background:
                button.value === activeTool && button.value !== ""
                  ? "linear-gradient(to bottom,#e7cb95,#c6b187)"
                  : "",
            }}
            key={button.value}
            onClick={() => onButtonClick(button.value)}
          >
            {button.label}
          </button>
        ))}
      </div>
      {activeTool !== "" && (
        <div>
          <h4 className="text-md mt-2">Measurement Result</h4>
          <hr className="mb-3" />
          {activeTool === "area" && (
            <Area
              distance={distance}
              selectedDistanceUnits={selectedDistanceUnits}
              setSelectedDistanceUnits={setSelectedDistanceUnits}
              selectedAreaUnits={selectedAreaUnits}
              setSelectedAreaUnits={setSelectedAreaUnits}
              area={area}
            />
          )}
          {activeTool === "distance" && (
            <Distance
              distance={distance}
              selectedDistanceUnits={selectedDistanceUnits}
              setSelectedDistanceUnits={setSelectedDistanceUnits}
            />
          )}
          {activeTool === "location" && <Location lat={lat} long={long} />}
        </div>
      )}
    </BaseDraggable>
  );
}
